import Box from 'ext-ui/components/Box/Box'
import Tooltip from 'ext-ui/components/Tooltip/Tooltip'
import { spacing } from 'ext-ui/style/spacing'
import ChevronIcon from 'ext-ui/svg/icon/ChevronIcon'
import React, { ReactElement } from 'react'
import ReceiptRecoveryTooltipIcon from 'src/assets/svgs/ReceiptRecoveryTooltipIcon.svg'
import CurrentResponsibilityIcon from 'src/feature/ReceiptRecovery/CurrentResponsibilityIcon'
import InterventionQueueIcon from 'src/feature/ReceiptRecovery/InterventionQueueIcon'
import { RECEIPT_WORK_GROUPS } from 'src/feature/ReceiptRecovery/receiptRecovery.fixtures'
import {
  ReceiptRecoveryAccountingPeriod,
  ReceiptRecoveryStats,
  ReceiptRecoveryTransactionState,
  ReceiptState,
} from 'src/feature/ReceiptRecovery/receiptRecovery.types'
import { useTranslate } from 'src/localization/Trns'

import * as style from './ReceiptRecoveryOverview.style'

interface ReceiptRecoveryOverviewProps {
  accountingPeriods: readonly ReceiptRecoveryAccountingPeriod[]
  onOpenTransactions: (state: ReceiptRecoveryTransactionState) => void
  onPeriodSelect: (periodId: string) => void
  receiptStats: ReceiptRecoveryStats
  selectedPeriodId: string
}

interface QueueSignalCopy {
  action?: string
  detail: string
  title: string
}

const ReceiptRecoveryOverview = ({
  accountingPeriods,
  onOpenTransactions,
  onPeriodSelect,
  receiptStats,
  selectedPeriodId,
}: ReceiptRecoveryOverviewProps): ReactElement => {
  const translate = useTranslate()

  const getStateLabel = (state: ReceiptState): string => {
    switch (state) {
      case 'needs-review':
        return translate('Needs Review')
      case 'waiting-on-spender':
        return translate('Waiting on Spender')
      case 'receipt-reconciled':
        return translate('Receipt Reconciled')
    }
  }

  const getStatusTooltipCopy = (state: ReceiptState): string => {
    switch (state) {
      case 'needs-review':
        return translate(
          'The Receipt Agent found a possible match. Review the receipt and confirm whether it belongs to the transaction.',
        )
      case 'receipt-reconciled':
        return translate(
          'A receipt has been confirmed and attached to the transaction.',
        )
      case 'waiting-on-spender':
        return translate(
          'The spender has been asked to provide the missing receipt.',
        )
    }
  }

  const getQueueSignalCopy = (
    state: Exclude<ReceiptState, 'receipt-reconciled'>,
    count: number,
  ): QueueSignalCopy => {
    switch (state) {
      case 'needs-review':
        return {
          title: translate('${count} recovered receipts need your review', {
            count: String(count),
          }),
          detail: translate(
            'Confirm whether each receipt matches its transaction',
          ),
          action: translate('View →'),
        }
      case 'waiting-on-spender':
        return {
          title:
            count === 1
              ? translate('${count} receipt is awaiting spender action', {
                  count: String(count),
                })
              : translate('${count} receipts are awaiting spender action', {
                  count: String(count),
                }),
          detail: translate(
            'The spender has been asked to provide the receipt.',
          ),
          action: translate('View →'),
        }
    }
  }

  const receiptWorkGroups = RECEIPT_WORK_GROUPS.map((group) => ({
    ...group,
    total:
      group.state === 'needs-review'
        ? receiptStats.needsReview
        : group.state === 'waiting-on-spender'
          ? receiptStats.waitingOnSpender
          : receiptStats.receiptReconciled,
  }))
  const openCount = receiptWorkGroups
    .filter(({ state }) => state !== 'receipt-reconciled')
    .reduce((total, group) => total + group.total, 0)

  return (
    <div css={style.overview}>
      <section
        aria-labelledby="current-responsibility-heading"
        className="currentResponsibilitySection"
      >
        <Box className="overviewCard responsibilityCard" padding={spacing(5)}>
          <div className="responsibilityHeader">
            <div>
              <h2 id="current-responsibility-heading">
                {translate('What needs attention')}
              </h2>
              <p>{translate('Stay on top of outstanding receipts.')}</p>
            </div>
          </div>
          <ul className="responsibilityGrid">
            {receiptWorkGroups.map((group) => {
              const stateLabel = getStateLabel(group.state)
              const tooltipCopy = getStatusTooltipCopy(group.state)

              return (
                <li key={group.state}>
                  <div className="responsibilityTile">
                    <button
                      aria-label={translate('View ${state} transactions', {
                        state: stateLabel,
                      })}
                      className="responsibilityTileAction"
                      type="button"
                      onClick={() => onOpenTransactions(group.state)}
                    />
                    <div className="responsibilityTileContent">
                      <div className="tileLabelRow">
                        <div className="tileLabel">
                          <CurrentResponsibilityIcon state={group.state} />
                          <span>{stateLabel}</span>
                        </div>
                        <Tooltip
                          placement="top-end"
                          tooltipContent={tooltipCopy}
                        >
                          <button
                            aria-label={translate(
                              'More information about ${state}',
                              { state: stateLabel },
                            )}
                            className="statusTooltipTrigger"
                            type="button"
                          >
                            <img
                              alt=""
                              aria-hidden="true"
                              src={ReceiptRecoveryTooltipIcon}
                            />
                          </button>
                        </Tooltip>
                      </div>
                      <div
                        className="responsibilityTileTotal"
                        data-state={group.state}
                      >
                        {group.total}
                      </div>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        </Box>
      </section>

      <div className="overviewColumns">
        <section aria-labelledby="accounting-period-heading">
          <h2 className="sectionLabel" id="accounting-period-heading">
            {translate('ACCOUNTING PERIOD')}
          </h2>
          <Box noPadding className="overviewCard periodCard">
            <ul>
              {accountingPeriods.map((period) => (
                <li
                  className="periodRow"
                  data-selected={period.id === selectedPeriodId}
                  key={period.id}
                >
                  <button
                    aria-current={
                      period.id === selectedPeriodId ? 'true' : undefined
                    }
                    className="periodButton"
                    type="button"
                    onClick={() => onPeriodSelect(period.id)}
                  >
                    <div>
                      <div className="periodLabel">{period.label}</div>
                      <span>
                        {period.id === selectedPeriodId
                          ? translate('In view · ${count} open', {
                              count: String(openCount),
                            })
                          : period.summary
                            ? period.summary
                            : ''}
                      </span>
                    </div>
                    {period.id !== selectedPeriodId && (
                      <ChevronIcon
                        className="periodChevron"
                        direction="right"
                        aria-hidden="true"
                        focusable="false"
                        role="presentation"
                      />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </Box>
        </section>

        <section aria-labelledby="work-queue-heading">
          <h2 className="sectionLabel" id="work-queue-heading">
            {translate('WORK QUEUE')}
          </h2>
          <Box noPadding className="overviewCard queueCard">
            <div className="queueHeader">
              <h3>{translate('What needs attention')}</h3>
              <p>{translate('Stay on top of outstanding receipts.')}</p>
            </div>
            <ul>
              {receiptWorkGroups
                .filter(({ state }) => state !== 'receipt-reconciled')
                .map((group) => {
                  const state = group.state as Exclude<
                    ReceiptState,
                    'receipt-reconciled'
                  >
                  const copy = getQueueSignalCopy(state, group.total)

                  return (
                    <li className="queueSignal" data-state={state} key={state}>
                      <div className="queueIcon">
                        <InterventionQueueIcon state={state} />
                      </div>
                      <div className="queueCopy">
                        <div className="queueCopyTitle">{copy.title}</div>
                        <span>{copy.detail}</span>
                      </div>
                      {copy.action && (
                        <button
                          aria-label={translate('View ${state} work queue', {
                            state: getStateLabel(state),
                          })}
                          className="linkButton"
                          type="button"
                          onClick={() => onOpenTransactions(state)}
                        >
                          {copy.action}
                        </button>
                      )}
                    </li>
                  )
                })}
            </ul>
          </Box>
        </section>
      </div>
    </div>
  )
}

export default ReceiptRecoveryOverview
